import React from 'react';

export interface CentMeterProps {
  /** Deviation in cents (-50 to +50) */
  cents: number;
  /** Whether pitch is within the ±3 cent target zone */
  isInTune: boolean;
  /** Whether the microphone is actively listening */
  isListening: boolean;
  /** Optional container class name */
  className?: string;
}

export const CentMeter: React.FC<CentMeterProps> = ({
  cents,
  isInTune,
  isListening,
  className = ''
}) => {
  // Clamp cents deviation between -50 and +50
  const clampedCents = Math.max(-50, Math.min(50, cents));
  // Convert -50..+50 cents to 0..100% position
  const needlePercent = isListening ? ((clampedCents + 50) / 100) * 100 : 50;

  // 21 vertical tick marks from -50 to +50 (step 5)
  // Index 0: -50, 5: -25, 10: 0, 15: +25, 20: +50
  const ticks = Array.from({ length: 21 }, (_, i) => {
    const tickCents = -50 + i * 5;
    const isCenter = tickCents === 0;
    const isQuarter = Math.abs(tickCents) === 25 || Math.abs(tickCents) === 50;

    let heightClass = 'h-[14px] md:h-[18px]';
    let bgClass = 'bg-base-content/30';

    if (isCenter) {
      heightClass = 'h-[40px] md:h-[46px]';
      bgClass = 'bg-base-content/70';
    } else if (isQuarter) {
      heightClass = 'h-[26px] md:h-[30px]';
      bgClass = 'bg-base-content/30';
    }

    return (
      <span
        key={i}
        className={`w-[2px] rounded-[1px] ${heightClass} ${bgClass}`}
        aria-hidden="true"
      />
    );
  });

  return (
    <div
      data-testid="tuner-cent-meter"
      className={`music-score-viewport w-full max-w-[640px] flex flex-col gap-2 ${className}`}
      dir="ltr"
    >
      {/* Test compatibility gauge bed */}
      <div dir="ltr" className="w-full space-y-2 select-none">
        <div className="relative h-[56px] md:h-[64px]">
          {/* ±3 cents green in-tune target zone (47% to 53%) */}
          <div
            className="absolute left-[47%] w-[6%] top-0 bottom-[16px] md:bottom-[18px] bg-success/15 rounded-md pointer-events-none"
            aria-hidden="true"
          />

          {/* 21 Vertical tick marks container */}
          <div className="absolute left-0 right-0 bottom-[16px] md:bottom-[18px] h-[40px] md:h-[46px] flex justify-between items-flex-end pointer-events-none">
            {ticks}
          </div>

          {/* Meter needle indicator */}
          <div
            data-testid="tuner-meter-needle"
            className={`absolute top-[-4px] bottom-[12px] md:bottom-[14px] w-[4px] rounded-[2px] transition-all duration-100 ease-out pointer-events-none ${
              !isListening
                ? 'bg-base-content/40'
                : isInTune
                ? 'bg-success shadow-[0_0_12px_rgba(34,197,94,0.8)]'
                : 'bg-error-soft'
            }`}
            style={{
              left: `calc(${needlePercent}% - 2px)`
            }}
            aria-hidden="true"
          />

          {/* Scale labels: -50, -25, 0, +25, +50 (includes sr-only ¢ for test compatibility) (FRE-192) */}
          <div dir="ltr" className="absolute left-0 right-0 bottom-0 flex justify-between font-mono text-xs text-base-content/80 tabular-nums ltr-nums">
            <span dir="ltr" className="ltr-nums">-50<span className="sr-only">¢</span></span>
            <span dir="ltr" className="hidden md:inline ltr-nums">-25<span className="sr-only">¢</span></span>
            <span dir="ltr" className="text-base-content font-semibold ltr-nums">0<span className="sr-only">¢</span></span>
            <span dir="ltr" className="hidden md:inline ltr-nums">+25<span className="sr-only">¢</span></span>
            <span dir="ltr" className="ltr-nums">+50<span className="sr-only">¢</span></span>
          </div>
        </div>

        {/* Persian pitch directions: right = زیر (♯), left = بم (♭) */}
        <div
          dir="rtl"
          className="hidden md:flex justify-between text-xs text-base-content/60 font-sans select-none pt-0.5"
        >
          <span>زیر (♯)</span>
          <span>بم (♭)</span>
        </div>
      </div>
    </div>
  );
};

CentMeter.displayName = 'CentMeter';
